import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { LIST_GC_TIME, LIST_STALE_TIME } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import type { JobPosition, JobPosting, Application } from '@/types';
import { mergeApplicationsForAnalytics } from '@/lib/applicationArchive';

export interface PortfolioAiCredits {
  totalGranted: number;
  totalRemaining: number;
  totalUsed: number;
  anyUnlimited: boolean;
  companiesLoaded: number;
}

export type AgencyPortfolioSnapshot = {
  positions: JobPosition[];
  postings: JobPosting[];
  applications: Application[];
  companyNameById: Record<string, string>;
  portfolioAiCredits: PortfolioAiCredits;
  companiesLoaded: number;
  companiesTotal: number;
};

export interface AgencyAggregatedData extends AgencyPortfolioSnapshot {
  /** True only on first load (no cached snapshot yet). */
  loading: boolean;
  /** Background refresh while showing cached portfolio. */
  isFetching: boolean;
  error: string | null;
}

const EMPTY_PORTFOLIO: AgencyPortfolioSnapshot = {
  positions: [],
  postings: [],
  applications: [],
  companyNameById: {},
  portfolioAiCredits: {
    totalGranted: 0,
    totalRemaining: 0,
    totalUsed: 0,
    anyUnlimited: false,
    companiesLoaded: 0,
  },
  companiesLoaded: 0,
  companiesTotal: 0,
};

type AgencyClientRef = { companyId: string; name: string };

/** Chart sample size per client — avoids full-company hydrate on agency portfolio load. */
const AGENCY_PORTFOLIO_ACTIVE_MAX_PAGES = 4;
const AGENCY_PORTFOLIO_ARCHIVED_MAX_PAGES = 2;

async function fetchAgencyPortfolio(
  activeClients: AgencyClientRef[],
): Promise<AgencyPortfolioSnapshot> {
  const companyNameById: Record<string, string> = {};
  for (const c of activeClients) companyNameById[c.companyId] = c.name;

  type CompanyBundle = {
    positions: JobPosition[];
    postings: JobPosting[];
    applications: Application[];
    ai: { granted: number; remaining: number; unlimited: boolean };
  };

  const loadOne = async (companyId: string): Promise<CompanyBundle> => {
    try {
      const pos = await api.positions.list(companyId);
      const taggedPositions = pos.map((p) => ({ ...p, _companyId: companyId }));
      const [companyPostings, activeApps, archivedApps, billing] = await Promise.all([
        api.postings.listAll(companyId, pos),
        api.applications.listByCompanyBounded(companyId, pos, {
          archived: false,
          maxPages: AGENCY_PORTFOLIO_ACTIVE_MAX_PAGES,
        }),
        api.applications.listByCompanyBounded(companyId, pos, {
          archived: true,
          maxPages: AGENCY_PORTFOLIO_ARCHIVED_MAX_PAGES,
        }).catch(() => [] as Application[]),
        api.billing.getStatus(companyId).catch(() => null),
      ]);
      const aiBalance = (billing?.balances as Record<string, {
        granted: number;
        remaining: number;
        unlimited: boolean;
      }> | undefined)?.['ai_company'];
      return {
        positions: taggedPositions,
        postings: companyPostings,
        applications: mergeApplicationsForAnalytics(activeApps, archivedApps),
        ai: {
          granted: aiBalance?.granted ?? 0,
          remaining: aiBalance?.remaining ?? 0,
          unlimited: aiBalance?.unlimited ?? !!billing?.isUnlimited,
        },
      };
    } catch (err) {
      console.error(`Failed to load data for company ${companyId}:`, err);
      return {
        positions: [],
        postings: [],
        applications: [],
        ai: { granted: 0, remaining: 0, unlimited: false },
      };
    }
  };

  const bundles = await Promise.all(
    activeClients.map((c) => loadOne(c.companyId)),
  );

  const allPositions: JobPosition[] = [];
  const allPostings: JobPosting[] = [];
  const allApplications: Application[] = [];
  let totalGranted = 0;
  let totalRemaining = 0;
  let anyUnlimited = false;
  let billingLoaded = 0;

  for (const b of bundles) {
    allPositions.push(...b.positions);
    allPostings.push(...b.postings);
    allApplications.push(...b.applications);
    totalGranted += b.ai.granted;
    totalRemaining += b.ai.remaining;
    if (b.ai.unlimited) anyUnlimited = true;
    if (b.ai.granted > 0 || b.ai.remaining > 0 || b.ai.unlimited) billingLoaded += 1;
  }

  return {
    positions: allPositions,
    postings: allPostings,
    applications: allApplications,
    companyNameById,
    portfolioAiCredits: {
      totalGranted,
      totalRemaining,
      totalUsed: Math.max(0, totalGranted - totalRemaining),
      anyUnlimited,
      companiesLoaded: billingLoaded,
    },
    companiesLoaded: activeClients.length,
    companiesTotal: activeClients.length,
  };
}

/**
 * Aggregates data from ALL active client companies (agency owner).
 * Cached in React Query — tab switches on Dashboard reuse the snapshot (no full fan-out).
 */
export function useAgencyAggregatedData(options?: { enabled?: boolean }): AgencyAggregatedData {
  const { user } = useAuth();
  const enabled = options?.enabled !== false && user?.accountType === 'agency_owner';

  const activeClients = useMemo(
    () => (user?.agency?.clients ?? []).filter((c) => c.isActive),
    [user?.agency?.clients],
  );
  const cohortKey = useMemo(
    () => activeClients.map((c) => c.companyId).sort().join(','),
    [activeClients],
  );

  const query = useQuery({
    queryKey: queryKeys.agencyPortfolio(cohortKey),
    queryFn: () => fetchAgencyPortfolio(activeClients),
    enabled: enabled && activeClients.length > 0,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });

  const snapshot = query.data ?? EMPTY_PORTFOLIO;

  return {
    ...snapshot,
    loading: query.isPending && query.data === undefined,
    isFetching: query.isFetching,
    error: query.error
      ? (query.error instanceof Error ? query.error.message : 'Failed to load aggregated data')
      : null,
  };
}
